import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { FiPlus, FiTrash2, FiPlay, FiPause, FiSettings, FiToggleLeft, FiToggleRight } from 'react-icons/fi';

export default function TradingControl() {
  const [pairs, setPairs] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    exchange_name: 'binanceus',
    symbol: 'BTC/USDT',
    strategy: 'RSI',
    is_paper_trading: true,
    position_size: 100,
    stop_loss_pct: 2,
    take_profit_pct: 5
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pairsRes, exchangesRes, strategiesRes] = await Promise.all([
        api.get('/trading-pairs'),
        api.get('/exchanges'),
        api.get('/strategies')
      ]);
      setPairs(pairsRes.data);
      setExchanges(exchangesRes.data);
      setStrategies(strategiesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPair = async (e) => {
    e.preventDefault();
    try {
      await api.post('/trading-pairs', formData);
      toast.success('Trading pair added successfully');
      setShowAddDialog(false);
      fetchData();
      setFormData({
        exchange_name: 'binanceus',
        symbol: 'BTC/USDT',
        strategy: 'RSI',
        is_paper_trading: true,
        position_size: 100,
        stop_loss_pct: 2,
        take_profit_pct: 5
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add pair');
    }
  };

  const togglePair = async (pairId) => {
    try {
      await api.patch(`/trading-pairs/${pairId}/toggle`);
      toast.success('Pair status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle pair');
    }
  };

  const toggleMode = async (pairId, currentMode, isActive) => {
    if (isActive) {
      toast.error('Stop the pair before switching modes');
      return;
    }
    
    const newMode = currentMode ? 'LIVE' : 'PAPER';

    if (!currentMode && exchanges.length === 0) {
      toast.error('Cannot switch to LIVE mode. Add exchange API keys first.');
      return;
    }
    
    if (window.confirm(`Switch to ${newMode} trading mode?`)) {
      try {
        await api.patch(`/trading-pairs/${pairId}/toggle-mode`);
        toast.success(`Switched to ${newMode} trading mode`);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to switch mode');
      }
    }
  };

  const deletePair = async (pairId) => {
    if (!window.confirm('Delete this pair?')) return;
    try {
      await api.delete(`/trading-pairs/${pairId}`);
      toast.success('Pair deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pair');
    }
  };

  if (loading) return <div className="text-text-muted">Loading...</div>;

  const activePairs = pairs.filter(p => p.is_active).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading text-text-main">TRADING CONTROL</h1>
          <p className="text-sm text-text-muted mt-1">
            Active Pairs: <span className={activePairs >= 15 ? 'text-destructive' : 'text-secondary'}>{activePairs}/15</span>
            {exchanges.length === 0 && ' | Paper trading only (no exchange connected)'}
          </p>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase px-6 py-3 rounded-sm shadow-lg"
        >
          <FiPlus className="inline mr-2" /> ADD PAIR
        </button>
      </div>

      <div className="bg-surface border border-border/40 rounded-sm p-6">
        <h2 className="text-xl font-bold uppercase font-heading mb-6">Active Trading Pairs</h2>
        <div className="space-y-3">
          {pairs.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <FiSettings size={48} className="mx-auto mb-4 opacity-30" />
              <p className="font-medium">No trading pairs configured</p>
              <p className="text-sm mt-2">Add your first pair to start trading</p>
              <p className="text-xs mt-3 text-accent-orange">💡 Tip: Paper trading works without exchange API keys!</p>
            </div>
          ) : (
            pairs.map(pair => (
              <div
                key={pair.pair_id}
                className="flex items-center justify-between p-5 bg-surface-highlight border border-border/40 rounded-sm hover:border-primary/20"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl font-mono font-bold text-text-main">{pair.symbol}</span>
                    <span
                      className={`text-xs px-3 py-1 rounded uppercase font-bold ${
                        pair.is_active
                          ? 'bg-secondary/10 text-secondary border border-secondary/20'
                          : 'bg-border text-text-muted'
                      }`}
                    >
                      {pair.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                    <button
                      onClick={() => toggleMode(pair.pair_id, pair.is_paper_trading, pair.is_active)}
                      disabled={pair.is_active}
                      className={`flex items-center gap-1 text-xs px-3 py-1 rounded uppercase font-bold transition-all ${
                        pair.is_paper_trading
                          ? 'bg-accent-orange/10 text-accent-orange hover:bg-accent-orange/20'
                          : 'bg-primary/10 text-primary hover:bg-primary/20'
                      } ${pair.is_active ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      title={pair.is_active ? 'Stop pair to switch modes' : 'Click to toggle'}
                    >
                      {pair.is_paper_trading ? (
                        <>
                          <FiToggleLeft className="text-lg" /> PAPER
                        </>
                      ) : (
                        <>
                          <FiToggleRight className="text-lg" /> LIVE
                        </>
                      )}
                    </button>
                  </div>
                  <div className="flex gap-6 text-xs text-text-muted">
                    <span>
                      Exchange: <span className="text-text-main font-medium">{pair.exchange_name}</span>
                    </span>
                    <span>
                      Strategy: <span className="text-text-main font-medium">{pair.strategy}</span>
                    </span>
                    <span>
                      Size: <span className="text-text-main font-medium">${pair.risk_params?.position_size}</span>
                    </span>
                    <span>
                      SL: <span className="text-destructive font-medium">{pair.risk_params?.stop_loss_pct}%</span>
                    </span>
                    <span>
                      TP: <span className="text-secondary font-medium">{pair.risk_params?.take_profit_pct}%</span>
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => togglePair(pair.pair_id)}
                    className={`uppercase text-xs font-bold px-4 py-2 rounded-sm ${
                      pair.is_active
                        ? 'bg-destructive/10 text-destructive border border-destructive/50 hover:bg-destructive/20'
                        : 'bg-secondary/10 text-secondary border border-secondary/20 hover:bg-secondary/20'
                    }`}
                  >
                    {pair.is_active ? (
                      <>
                        <FiPause className="inline mr-1" /> STOP
                      </>
                    ) : (
                      <>
                        <FiPlay className="inline mr-1" /> START
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => deletePair(pair.pair_id)}
                    className="bg-destructive/10 text-destructive border border-destructive/50 hover:bg-destructive/20 px-3 py-2 rounded-sm"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showAddDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setShowAddDialog(false)}>
          <div className="bg-surface border border-border max-w-md w-full rounded-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold font-heading text-primary mb-6">Add Trading Pair</h2>
            <form onSubmit={handleAddPair} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-text-main">Exchange</label>
                <select
                  value={formData.exchange_name}
                  onChange={(e) => setFormData({ ...formData, exchange_name: e.target.value })}
                  className="w-full px-4 py-2 bg-black/50 border border-border text-text-main rounded-sm"
                  required
                >
                  <option value="binanceus">Binance.us</option>
                  <option value="coinbase">Coinbase</option>
                  <option value="kraken">Kraken</option>
                </select>
                <p className="text-xs text-text-muted mt-1">Paper trading uses public data (no API keys needed)</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-main">Symbol</label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  className="w-full px-4 py-2 bg-black/50 border border-border text-text-main rounded-sm"
                  placeholder="BTC/USDT"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-main">Strategy</label>
                <select
                  value={formData.strategy}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      strategy: e.target.value
                    })
                  }
                  className="w-full px-4 py-2 bg-black/50 border border-border text-text-main rounded-sm"
                >
                  {/* Built-in strategies */}
                  <option value="RSI">RSI Oversold/Overbought</option>
                  <option value="MACD">MACD Crossover</option>
                  <option value="EMA_CROSSOVER">EMA Crossover</option>
                  <option value="BOLLINGER_BANDS">Bollinger Bands</option>

                  {/* Custom strategies */}
                  {strategies.map((strat) => (
                    <option key={strat.strategy_id} value={strat.name}>
                      {strat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 p-3 bg-accent-orange/10 border border-accent-orange/20 rounded-sm">
                <input
                  type="checkbox"
                  checked={formData.is_paper_trading}
                  onChange={(e) => setFormData({ ...formData, is_paper_trading: e.target.checked })}
                  className="w-4 h-4"
                />
                <label className="text-sm text-text-main font-medium">
                  Start in Paper Trading Mode (Recommended)
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs mb-1 text-text-muted">Position ($)</label>
                  <input
                    type="number"
                    value={formData.position_size}
                    onChange={(e) => setFormData({ ...formData, position_size: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 bg-black/50 border border-border text-text-main rounded-sm"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1 text-text-muted">Stop Loss %</label>
                  <input
                    type="number"
                    value={formData.stop_loss_pct}
                    onChange={(e) => setFormData({ ...formData, stop_loss_pct: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 bg-black/50 border border-border text-text-main rounded-sm"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1 text-text-muted">Take Profit %</label>
                  <input
                    type="number"
                    value={formData.take_profit_pct}
                    onChange={(e) => setFormData({ ...formData, take_profit_pct: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 bg-black/50 border border-border text-text-main rounded-sm"
                    step="0.1"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase py-2 rounded-sm"
                >
                  ADD PAIR
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddDialog(false)}
                  className="px-6 bg-border text-text-main hover:bg-border/80 font-bold uppercase py-2 rounded-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
